import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { PiArrowDownBold, PiArrowUpBold, PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import { Button, DESTRUCTIVE_GHOST_BUTTON_CLASS } from '#/modules/commonComponents/button';
import { ActionHintIcon } from '#/modules/commonComponents/hint';
import { createEmptyExpenseRow } from '#/modules/momMgmt/workOrder/helper/workOrderExpenses';

/** 名稱｜金額｜排序／移除 */
const LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(10rem,20rem)_minmax(8rem,12rem)_auto]';

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-[var(--z-content-sticky)]',
  'hidden w-full max-w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid @3xl/form-section:w-max',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full max-w-full grid-cols-1 gap-2 px-3 py-2.5',
  '@3xl/form-section:w-max @3xl/form-section:items-center',
  LINE_GRID_COLS,
);

const KEEP_EXISTING_HINT = '已建立的費用不可移除；不再收取請把金額改為 0';

/**
 * 工單費用編輯（TWD）。既有項目（有 expenseId）不可移除，只能改名稱／金額／順序。
 * `name` 為 RHF 陣列欄位名（工單表單 `expenses`）。
 */
const WorkOrderExpensesEditor = ({
  control,
  register,
  errors,
  name = 'expenses',
  disabled = false,
  description,
}) => {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name,
    keyName: 'fieldKey',
  });

  return (
    <div className="form-section @container/form-section w-full">
      <div className="form-section__header">
        <h2 className="form-block__title">費用</h2>
        {!disabled ? (
          <Button type="button" size="sm" onClick={() => append(createEmptyExpenseRow())}>
            新增費用
          </Button>
        ) : null}
      </div>
      {description ? (
        <p className="form-block__description">{description}</p>
      ) : null}

      {fields.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">尚無費用</p>
      ) : (
        <div className="form-section__scroll">
          <div className={LINE_HEADER_CLASS} aria-hidden>
            <p>名稱</p>
            <p>金額（TWD）</p>
            <p className="justify-self-end text-right">排序／移除</p>
          </div>
          <ul className="m-0 w-full max-w-full list-none divide-y divide-gray-200 p-0 @3xl/form-section:w-max">
            {fields.map((field, idx) => {
              const isExisting = Boolean(field.expenseId);
              return (
                <li key={field.fieldKey} className={LINE_ROW_CLASS}>
                  <PlainRegisteredInput
                    id={`${name}.${idx}.name`}
                    register={register}
                    errors={errors}
                    placeholder="例如：開機費"
                    mobileLabel={`名稱 #${idx + 1}`}
                    ariaLabel={`費用名稱 ${idx + 1}`}
                    readOnly={disabled}
                  />
                  <PlainRegisteredInput
                    id={`${name}.${idx}.amount`}
                    register={register}
                    errors={errors}
                    type="number"
                    min={0}
                    step="any"
                    placeholder="0"
                    mobileLabel="金額（TWD）"
                    ariaLabel={`費用金額 ${idx + 1}`}
                    readOnly={disabled}
                  />
                  <div className="flex min-w-0 items-center justify-end gap-1">
                    {!disabled ? (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`上移費用 ${idx + 1}`}
                          disabled={idx === 0}
                          onClick={() => move(idx, idx - 1)}
                        >
                          <PiArrowUpBold className="size-4" aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`下移費用 ${idx + 1}`}
                          disabled={idx === fields.length - 1}
                          onClick={() => move(idx, idx + 1)}
                        >
                          <PiArrowDownBold className="size-4" aria-hidden />
                        </Button>
                        {isExisting ? (
                          <ActionHintIcon
                            tip={KEEP_EXISTING_HINT}
                            ariaLabel={`費用 ${idx + 1} 不可移除說明`}
                            className="mx-1.5"
                          />
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                            aria-label={`移除費用 ${idx + 1}`}
                            onClick={() => remove(idx)}
                          >
                            <PiTrashBold className="size-4" aria-hidden />
                          </Button>
                        )}
                      </>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

WorkOrderExpensesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  description: PropTypes.node,
};

export default WorkOrderExpensesEditor;
